import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon, Logo, socials } from '@byaft/ui';
import { CYBER_URL, TOOLS_URL } from '../siteConfig';
import { getFilmNavState, subscribeFilmNav } from '../features/film/filmNav';

interface NavItem {
  label: string;
  /** Internal route (react-router). */
  to?: string;
  /** External absolute URL (sibling BYAFT site). */
  href?: string;
}

const navLinks: NavItem[] = [
  { label: 'About', to: '/about' },
  { label: 'Tools', href: TOOLS_URL },
  { label: 'Cyber', href: CYBER_URL },
];

const linkClass =
  'font-display text-sm font-bold uppercase tracking-wide text-on-surface/90 transition-colors hover:text-primary-container';

/**
 * Fixed header. Like the tools navbar, its solid background is suppressed while a
 * scroll-film is playing (active but unfinished) so it stays transparent over the
 * cinematic intro, then fades in once the film finishes / on other pages. Below
 * `sm` the links collapse into a hamburger menu so the bar never overflows.
 */
export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [filmOverlay, setFilmOverlay] = useState(() => {
    const s = getFilmNavState();
    return s.active && !s.finished;
  });
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => subscribeFilmNav((s) => setFilmOverlay(s.active && !s.finished)), []);

  // Close the mobile menu whenever the route changes. Block body on purpose — a
  // concise arrow would hand this effect's return value to React as its cleanup,
  // which React invokes on the next navigation (see ScrollTop in App.tsx).
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Solid once scrolled (unless a film is still playing); the open menu forces solid.
  const solidBg = menuOpen || (scrolled && !filmOverlay);

  const renderLink = (item: NavItem, className: string, onClick?: () => void) =>
    item.href ? (
      <a key={item.label} href={item.href} className={className} onClick={onClick}>
        {item.label}
      </a>
    ) : (
      <Link key={item.label} to={item.to!} className={className} onClick={onClick}>
        {item.label}
      </Link>
    );

  return (
    <header
      className={[
        'fixed inset-x-0 top-0 z-50 transition-all duration-500',
        solidBg
          ? 'border-b border-glass-border bg-surface/80 backdrop-blur-xl'
          : 'border-b border-transparent bg-transparent',
      ].join(' ')}
    >
      <nav className="container-byaft flex items-center gap-5 py-5" aria-label="Primary">
        <Link to="/" className="rounded-md" aria-label="BYAFT home">
          <Logo />
        </Link>

        {/* Desktop links + socials */}
        <div className="ml-auto hidden items-center gap-5 sm:flex">
          {navLinks.map((item) => renderLink(item, linkClass))}
          <div className="flex items-center gap-1">
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={`BYAFT on ${s.label}`}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-on-surface/80 transition-colors hover:bg-white/5 hover:text-primary-container"
              >
                <Icon name={s.icon} size={18} />
              </a>
            ))}
          </div>
        </div>

        {/* Mobile menu toggle */}
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-expanded={menuOpen}
          aria-controls="site-mobile-menu"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          className="ml-auto inline-flex h-11 w-11 items-center justify-center rounded-full border border-glass-border bg-white/5 text-on-background transition-colors hover:border-primary-container/50 sm:hidden"
        >
          <Icon name={menuOpen ? 'close' : 'menu'} size={22} />
        </button>
      </nav>

      {/* Mobile dropdown */}
      <div
        id="site-mobile-menu"
        className={[
          'overflow-hidden border-t border-glass-border bg-surface/95 backdrop-blur-xl transition-[max-height,opacity] duration-400 sm:hidden',
          menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0',
        ].join(' ')}
      >
        <div className="container-byaft flex flex-col gap-1 py-4">
          {navLinks.map((item) =>
            renderLink(
              item,
              'rounded-lg px-3 py-3 font-display text-lg font-bold text-on-background transition-colors hover:bg-white/5 hover:text-primary-container',
              () => setMenuOpen(false),
            ),
          )}

          <div className="mt-2 flex items-center gap-2 border-t border-glass-border px-3 pt-4">
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={`BYAFT on ${s.label}`}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-glass-border bg-white/5 text-on-background transition-colors hover:border-primary-container/50 hover:text-primary-container"
              >
                <Icon name={s.icon} size={18} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}
